import { guidedSatinSegments } from "./satinPreviewGeometry";
/**
 * Pure geometry computation for stitch patterns.
 * Returns SVG-viewBox-coordinate line segments / path data
 * so the caller can render them as vector SVG elements.
 */

function samplePathPointsSync(d, numSamples) {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.style.position = "absolute";
  svg.style.width = "0";
  svg.style.height = "0";
  svg.style.overflow = "hidden";
  const path = document.createElementNS(ns, "path");
  path.setAttribute("d", d);
  svg.appendChild(path);
  document.body.appendChild(svg);
  const totalLen = path.getTotalLength();
  const pts = [];
  for (let i = 0; i <= numSamples; i++) {
    const pt = path.getPointAtLength((i / numSamples) * totalLen);
    pts.push({ x: pt.x, y: pt.y });
  }
  document.body.removeChild(svg);
  return pts;
}

// ── Satin ────────────────────────────────────────────────────────────────

/**
 * Compute satin stitch segments between two rails in SVG viewBox coords.
 * Includes both the cross-column pass and return to the next rail point.
 */
export function computeSatinStitchD(rail0D, rail1D, spacingMm, mmToPx, vbW, pullCompensation = 0, rungs = []) {
  if (!rail0D || !rail1D) return "";

  const numSamples = Math.max(40, Math.round(vbW / Math.max(0.5, spacingMm)));
  let r0, r1;
  try {
    r0 = samplePathPointsSync(rail0D, numSamples);
    r1 = samplePathPointsSync(rail1D, numSamples);
  } catch (_) {
    return "";
  }
  if (r0.length < 2 || r1.length < 2) return "";

  const guides = rungs.map((d) => {
    try { return samplePathPointsSync(d, 16); } catch (_) { return []; }
  }).filter((points) => points.length > 1);
  // zigzag_spacing is peak-to-peak (a full cycle), not one thread crossing.
  // Connect b to the next a as the export's standard satin does. Omitting that
  // return leg made the preview show only half of the top-thread coverage.
  return guidedSatinSegments(r0, r1, guides, Math.max(0.001, spacingMm), pullCompensation)
    .map(([a, b], i) => `${i === 0 ? "M" : "L"}${a.x.toFixed(3)},${a.y.toFixed(3)}L${b.x.toFixed(3)},${b.y.toFixed(3)}`)
    .join("");
}

// ── Fill (angled parallel lines) ─────────────────────────────────────────

/**
 * Compute fill stitch lines as a single `d` string in SVG viewBox coords.
 * Lines are rotated by `angleDeg` and cover the full viewBox area;
 * the caller clips them with the layer shape.
 */
export function computeFillStitchD(vbX, vbY, vbW, vbH, angleDeg, spacingMm) {
  const spacing = Math.max(0.3, spacingMm);
  const cx = vbX + vbW / 2, cy = vbY + vbH / 2;
  const diag = Math.sqrt(vbW * vbW + vbH * vbH);
  const rad = ((angleDeg ?? 45) * Math.PI) / 180;
  const cosA = Math.cos(rad), sinA = Math.sin(rad);

  let d = "";
  for (let offset = -diag; offset < diag; offset += spacing) {
    const x1 = cx + (-diag) * cosA - offset * sinA;
    const y1 = cy + (-diag) * sinA + offset * cosA;
    const x2 = cx + diag * cosA - offset * sinA;
    const y2 = cy + diag * sinA + offset * cosA;
    d += `M${x1.toFixed(2)},${y1.toFixed(2)}L${x2.toFixed(2)},${y2.toFixed(2)}`;
  }
  return d;
}

// ── Running stitch ───────────────────────────────────────────────────────

/**
 * For running stitch we just return the original path d-strings;
 * the SVG renderer uses strokeDasharray to create the stitch effect.
 */
export function computeRunningStitchDs(pathDStrings) {
  return (pathDStrings || []).filter(Boolean);
}

// ── Meander fill ─────────────────────────────────────────────────────────

/**
 * Compute a meander (serpentine) path in SVG viewBox coords.
 * Returns a single `d` string.
 */
export function computeMeanderStitchD(vbX, vbY, vbW, vbH, spacingMm) {
  const spacing = Math.max(0.3, spacingMm);
  const pitch = spacing * 1.7;
  const wobbleAmp = spacing * 0.5;
  const wobblePeriod = spacing * 8;
  const halfP = wobblePeriod / 2;
  const padX = spacing * 1.8;

  const minX = vbX, maxX = vbX + vbW, minY = vbY, maxY = vbY + vbH;
  const left = minX - padX, right = maxX + padX;

  const sineY = (x, yBase, rowPhase) =>
    yBase + wobbleAmp * Math.sin((x / wobblePeriod + rowPhase) * Math.PI * 2);

  let d = "";
  let goRight = true;
  let prevEndY = null;

  for (let row = 0, yBase = minY + pitch * 0.5; yBase <= maxY; yBase += pitch, row++) {
    const rowPhase = row * 0.37;
    const startX = goRight ? left : right;
    const endX = goRight ? right : left;
    const startY = sineY(startX, yBase, rowPhase);
    const endY = sineY(endX, yBase, rowPhase);

    if (prevEndY === null) {
      d += `M${startX.toFixed(2)},${startY.toFixed(2)}`;
    } else {
      const ext = goRight ? -spacing * 1.6 : spacing * 1.6;
      d += `C${(startX + ext).toFixed(2)},${prevEndY.toFixed(2)} ${(startX + ext).toFixed(2)},${startY.toFixed(2)} ${startX.toFixed(2)},${startY.toFixed(2)}`;
    }

    const dir = goRight ? 1 : -1;
    const n0 = goRight
      ? Math.ceil((startX / wobblePeriod + rowPhase) * 2)
      : Math.floor((startX / wobblePeriod + rowPhase) * 2);

    let curX = startX;
    const maxSegs = Math.ceil(Math.abs(endX - startX) / halfP) + 4;
    for (let s = 0, n = n0; s < maxSegs; s++, n += dir) {
      const zcX = (n / 2 - rowPhase) * wobblePeriod;
      const segEnd = goRight ? Math.min(zcX, endX) : Math.max(zcX, endX);
      const xMid = (curX + segEnd) / 2;
      const yCur = sineY(curX, yBase, rowPhase);
      const ySeg = sineY(segEnd, yBase, rowPhase);
      const yMid = sineY(xMid, yBase, rowPhase);
      const yCP = 2 * yMid - 0.5 * (yCur + ySeg);
      d += `Q${xMid.toFixed(2)},${yCP.toFixed(2)} ${segEnd.toFixed(2)},${ySeg.toFixed(2)}`;
      curX = segEnd;
      if (goRight ? curX >= endX : curX <= endX) break;
    }
    d += `L${endX.toFixed(2)},${endY.toFixed(2)}`;

    prevEndY = endY;
    goRight = !goRight;
  }
  return d;
}

// ── Contour fill ─────────────────────────────────────────────────────────

/**
 * Contour fill generates concentric rings following the shape outline.
 * Since the distance-transform approach requires pixel data, we approximate
 * with concentric offset paths: shrink the outline by `spacing` increments.
 * Returns an array of `d` strings for concentric rings.
 */
export function computeContourStitchDs(pathDs, vbX, vbY, vbW, vbH, spacingMm) {
  const spacing = Math.max(0.3, spacingMm);
  // Concentric rounded rings inset from the bounding box. Once clipped to the
  // layer shape these read as contour lines that follow the outline inward —
  // visually distinct from Auto Fill (straight rows) and Circular (true circles).
  const maxInset = Math.min(vbW, vbH) / 2;
  let d = "";
  for (let inset = spacing * 0.5; inset < maxInset; inset += spacing) {
    const x = vbX + inset;
    const y = vbY + inset;
    const w = vbW - inset * 2;
    const h = vbH - inset * 2;
    if (w <= 0 || h <= 0) break;
    const r = Math.min(w, h) * 0.32;
    d +=
      `M${(x + r).toFixed(2)},${y.toFixed(2)}` +
      `L${(x + w - r).toFixed(2)},${y.toFixed(2)}` +
      `Q${(x + w).toFixed(2)},${y.toFixed(2)} ${(x + w).toFixed(2)},${(y + r).toFixed(2)}` +
      `L${(x + w).toFixed(2)},${(y + h - r).toFixed(2)}` +
      `Q${(x + w).toFixed(2)},${(y + h).toFixed(2)} ${(x + w - r).toFixed(2)},${(y + h).toFixed(2)}` +
      `L${(x + r).toFixed(2)},${(y + h).toFixed(2)}` +
      `Q${x.toFixed(2)},${(y + h).toFixed(2)} ${x.toFixed(2)},${(y + h - r).toFixed(2)}` +
      `L${x.toFixed(2)},${(y + r).toFixed(2)}` +
      `Q${x.toFixed(2)},${y.toFixed(2)} ${(x + r).toFixed(2)},${y.toFixed(2)}Z`;
  }
  return d;
}

// ── Circular fill ────────────────────────────────────────────────────────

/**
 * Circular fill draws concentric circles from the shape center.
 * Returns a single `d` string of concentric circle arcs.
 */
export function computeCircularStitchD(vbX, vbY, vbW, vbH, spacingMm) {
  const spacing = Math.max(0.3, spacingMm);
  const cx = vbX + vbW / 2, cy = vbY + vbH / 2;
  const maxR = Math.sqrt(vbW * vbW + vbH * vbH) / 2;

  let d = "";
  for (let r = spacing; r < maxR; r += spacing) {
    // SVG arc: two half-circles to form a full circle
    d += `M${(cx - r).toFixed(2)},${cy.toFixed(2)}`;
    d += `A${r.toFixed(2)},${r.toFixed(2)} 0 1,1 ${(cx + r).toFixed(2)},${cy.toFixed(2)}`;
    d += `A${r.toFixed(2)},${r.toFixed(2)} 0 1,1 ${(cx - r).toFixed(2)},${cy.toFixed(2)}`;
  }
  return d;
}
